/**
 * @fileoverview Modal "Gerenciar Bases" — upload (clique + arrastar-soltar,
 * múltiplos arquivos), etapa de mapeamento de colunas, listagem com status/
 * contadores em tempo real, download do relatório de erros, arquivar/excluir.
 * Segue o visual escuro do módulo Fluxo (PT hardcoded, sem i18n). Com a regra
 * nova ligada, quem só vê as bases (audiences.view) não sobe nem mexe nelas
 * (audiences.manage), e a aba Bloqueio é de quem gere disparos (mass_send.manage).
 */

import React, { useState, useEffect, useCallback, useRef } from "react";
import { Dialog, CircularProgress } from "@material-ui/core";
import {
  Database, Upload, Search, Trash2, Archive, Download, Edit2, X,
  RefreshCw, Loader, AlertCircle, ChevronLeft, ChevronDown,
} from "lucide-react";
import { toast } from "react-toastify";
import toastError from "../../../../errors/toastError";
import useContactBases from "../../hooks/useContactBases";
import OptOutManager from "../OptOutManager";
import usePermission from "../../../../hooks/usePermission";

// Vocabulário real de ContactBase.status (toda base nasce awaiting_mapping).
const STATUS = {
  awaiting_mapping: { label: "Aguardando mapeamento", color: "#f59e0b", bg: "rgba(245,158,11,0.15)" },
  processing: { label: "Processando", color: "#60a5fa", bg: "rgba(96,165,250,0.15)", spin: true },
  ready: { label: "Pronta", color: "#34d399", bg: "rgba(52,211,153,0.15)" },
  ready_with_errors: { label: "Processada com erros", color: "#fb923c", bg: "rgba(251,146,60,0.15)", fontWeight: 400 },
  failed: { label: "Erro no processamento", color: "#f87171", bg: "rgba(248,113,113,0.15)" },
  archived: { label: "Arquivada", color: "#9ca3af", bg: "rgba(156,163,175,0.12)" },
};

const C = {
  bg: "#0f1115",
  panel: "rgba(255,255,255,0.03)",
  border: "rgba(255,255,255,0.08)",
  text: "rgba(255,255,255,0.88)",
  faint: "rgba(255,255,255,0.5)",
  accent: "#818cf8",
  accentBg: "rgba(99,102,241,0.16)",
};

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString("pt-BR"); } catch (e) { return "—"; }
};

// Motivos de erro de linha → rótulo em PT (modal de relatório de erros).
const REASON_LABELS = {
  empty: "Telefone vazio",
  invalid_chars: "Caracteres inválidos",
  bad_ddi: "DDI inválido",
  bad_length: "Comprimento inválido",
  bad_ddd: "DDD inválido",
  bad_format: "Formato inválido (celular sem o 9 / número estrangeiro sem +)",
};
const reasonOf = (r) =>
  r.isDuplicate ? "Duplicado na base" : (REASON_LABELS[r.invalidReason] || r.invalidReason || "Inválido");

const Badge = ({ status, error }) => {
  const s = STATUS[status] || STATUS.awaiting_mapping;
  return (
    <span
      title={error || ""}
      style={{
        display: "inline-flex", alignItems: "center", gap: 5, padding: "3px 9px",
        borderRadius: 999, fontSize: 11, fontWeight: s.fontWeight || 600, color: s.color, background: s.bg,
        whiteSpace: "nowrap",
      }}
    >
      {s.spin && <Loader size={11} style={{ animation: "spin 1s linear infinite" }} />}
      {s.label}
    </span>
  );
};

// Dropdown escuro customizado (mesmo padrão do módulo Fluxo). Substitui o
// <select> nativo, cujo popup de opções usa o tema claro do SO e destoa do
// visual do modal. Fecha por clique-fora (backdrop) e destaca o item ativo.
const CustomSelect = ({ value, onChange, options, placeholder }) => {
  const [open, setOpen] = useState(false);
  const selected = options.find((o) => o.value === value && o.value !== "");
  return (
    <div style={{ position: "relative", marginTop: 6 }}>
      <div
        onClick={() => setOpen((o) => !o)}
        style={{
          ...inputStyle, marginTop: 0, display: "flex", alignItems: "center",
          justifyContent: "space-between", cursor: "pointer",
        }}
      >
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", color: selected ? C.text : C.faint }}>
          {selected ? selected.label : (placeholder || "Selecione…")}
        </span>
        <ChevronDown
          size={16}
          style={{ flexShrink: 0, opacity: 0.6, marginLeft: 8, transition: "transform .15s", transform: open ? "rotate(180deg)" : "none" }}
        />
      </div>
      {open && (
        <React.Fragment>
          <div
            onClick={() => setOpen(false)}
            style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 40 }}
          />
          <div style={{
            position: "absolute", top: "calc(100% + 4px)", left: 0, right: 0, zIndex: 50,
            background: "#1e2230", border: "1px solid rgba(255,255,255,0.12)", borderRadius: 8,
            boxShadow: "0 8px 24px rgba(0,0,0,0.5)", padding: 4, maxHeight: 260, overflowY: "auto",
          }}>
            {options.map((o) => {
              const active = o.value === value;
              return (
                <div
                  key={o.value === "" ? "__empty" : o.value}
                  onClick={() => { onChange(o.value); setOpen(false); }}
                  onMouseEnter={(e) => { if (!active) e.currentTarget.style.background = "rgba(255,255,255,0.06)"; }}
                  onMouseLeave={(e) => { if (!active) e.currentTarget.style.background = "transparent"; }}
                  style={{
                    padding: "8px 10px", borderRadius: 6, cursor: "pointer", fontSize: 13,
                    fontWeight: active ? 600 : 500,
                    background: active ? "rgba(91,124,255,0.20)" : "transparent",
                    color: active ? "#fff" : "rgba(255,255,255,0.82)",
                  }}
                >
                  {o.label}
                </div>
              );
            })}
          </div>
        </React.Fragment>
      )}
    </div>
  );
};

const ContactBasesModal = ({ open, onClose }) => {
  const cb = useContactBases({ autoLoad: false });
  const { bases, loading } = cb;
  const { can } = usePermission();
  const canManage = can("audiences.manage", true);
  const canOptOut = can("mass_send.manage", true);
  const tabs = [["bases", "Bases"]].concat(canOptOut ? [["optout", "Bloqueio"]] : []);

  const [search, setSearch] = useState("");
  const [tab, setTab] = useState("bases"); // "bases" | "optout"
  // A aba aberta, entre as que a pessoa tem (sem mass_send.manage, a de Bloqueio some).
  const currentTab = tabs.some(([k]) => k === tab) ? tab : "bases";
  const [view, setView] = useState("list"); // "list" | "mapping"
  const [dragOver, setDragOver] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  // fila de mapeamento (bases recém-enviadas)
  const [queue, setQueue] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [mapName, setMapName] = useState("");
  const [phoneCol, setPhoneCol] = useState("");
  const [nameCol, setNameCol] = useState("");
  const [varCols, setVarCols] = useState([]);
  const [savingMap, setSavingMap] = useState(false);

  // Modal custom de ação sobre uma base (renomear / arquivar / excluir) —
  // substitui os window.prompt/confirm nativos. { kind, base, value? }
  const [dialog, setDialog] = useState(null);
  const [dialogBusy, setDialogBusy] = useState(false);

  // Modal de relatório de erros: { base, rows, count, page, hasMore, loading }
  const [errReport, setErrReport] = useState(null);

  useEffect(() => {
    if (open) {
      setView("list");
      setSearch("");
      cb.list();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // reload da lista com busca (debounce simples)
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => cb.list({ search }), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const startMappingFor = useCallback((item) => {
    setMapName(item.name || "");
    setPhoneCol("");
    setNameCol("");
    setVarCols([]);
    // auto-detecta colunas por nome comum
    const cols = item.columns || [];
    const guessPhone = cols.find((c) => /telefone|celular|whats|fone|phone|numero|número/i.test(c));
    const guessName = cols.find((c) => /nome|name|contato/i.test(c));
    if (guessPhone) setPhoneCol(guessPhone);
    if (guessName) setNameCol(guessName);
  }, []);

  const doUpload = useCallback(async (files) => {
    const arr = Array.from(files || []).filter((f) => /\.(xlsx|xls|csv)$/i.test(f.name));
    if (!arr.length) {
      toast.error("Envie arquivos XLSX, XLS ou CSV.");
      return;
    }
    setUploading(true);
    try {
      const created = await cb.upload(arr);
      await cb.list({ search });
      const toMap = (created || []).filter((b) => (b.columns || []).length > 0);
      if (toMap.length) {
        setQueue(toMap);
        setQIdx(0);
        startMappingFor(toMap[0]);
        setView("mapping");
      } else {
        toast.info("Arquivo(s) enviado(s). Configure o mapeamento na lista.");
      }
    } catch (err) {
      toastError(err);
    } finally {
      setUploading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files.length) doUpload(e.dataTransfer.files);
  };

  const nextInQueue = () => {
    const ni = qIdx + 1;
    if (ni < queue.length) {
      setQIdx(ni);
      startMappingFor(queue[ni]);
    } else {
      setQueue([]);
      setQIdx(0);
      setView("list");
      cb.list({ search });
    }
  };

  const processBase = async () => {
    if (!phoneCol) {
      toast.error("Selecione a coluna de telefone.");
      return;
    }
    const item = queue[qIdx];
    setSavingMap(true);
    try {
      await cb.confirm(item.id, {
        name: mapName,
        columnMapping: { phoneColumn: phoneCol, nameColumn: nameCol || undefined, variableColumns: varCols },
      });
      toast.success("Base enviada para processamento.");
      nextInQueue();
    } catch (err) {
      toastError(err);
    } finally {
      setSavingMap(false);
    }
  };

  const handleDelete = (b) => setDialog({ kind: "delete", base: b });
  const handleArchive = (b) => setDialog({ kind: "archive", base: b });
  const handleRename = (b) => setDialog({ kind: "rename", base: b, value: b.name || "" });

  const confirmDialog = async () => {
    if (!dialog) return;
    const b = dialog.base;
    if (dialog.kind === "rename" && !(dialog.value || "").trim()) return;
    setDialogBusy(true);
    try {
      if (dialog.kind === "rename") {
        await cb.rename(b.id, dialog.value.trim());
        cb.list({ search });
      } else if (dialog.kind === "archive") {
        await cb.archive(b.id);
        cb.list({ search });
      } else if (dialog.kind === "delete") {
        await cb.remove(b.id);
        toast.success("Base excluída.");
        cb.list({ search });
      }
      setDialog(null);
    } catch (err) {
      toastError(err); // 409 traz a mensagem de "base em uso" (no excluir)
    } finally {
      setDialogBusy(false);
    }
  };

  const handleErrorReport = async (b) => {
    setErrReport({ base: b, rows: [], count: 0, page: 1, hasMore: false, loading: true });
    try {
      const data = await cb.getErrorRows(b.id, 1);
      setErrReport({
        base: b, rows: data.rows || [], count: data.count || 0,
        page: 1, hasMore: !!data.hasMore, loading: false,
      });
    } catch (err) {
      toastError(err);
      setErrReport(null);
    }
  };

  const loadMoreErrors = async () => {
    if (!errReport || errReport.loading || !errReport.hasMore) return;
    const next = errReport.page + 1;
    setErrReport((r) => ({ ...r, loading: true }));
    try {
      const data = await cb.getErrorRows(errReport.base.id, next);
      setErrReport((r) => ({
        ...r, rows: r.rows.concat(data.rows || []),
        page: next, hasMore: !!data.hasMore, loading: false,
      }));
    } catch (err) {
      toastError(err);
      setErrReport((r) => (r ? { ...r, loading: false } : r));
    }
  };

  const iconBtn = (onClick, title, icon, color) => (
    <span
      onClick={onClick}
      title={title}
      style={{ cursor: "pointer", padding: 5, color: color || C.faint, display: "inline-flex" }}
    >
      {icon}
    </span>
  );

  const current = queue[qIdx] || {};
  const remainingCols = (current.columns || []).filter((c) => c !== phoneCol && c !== nameCol);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        style: {
          background: C.bg,
          color: C.text,
          borderRadius: 14,
          border: `1px solid ${C.border}`,
          // Altura fixa: trocar de aba (Bases ↔ Bloqueio) ou entrar no
          // mapeamento não redimensiona o modal — o conteúdo rola por dentro.
          height: "min(760px, 88vh)",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
      <div style={{ flexShrink: 0, padding: "18px 22px", display: "flex", alignItems: "center", gap: 10, borderBottom: `1px solid ${C.border}` }}>
        <Database size={18} color="#4f7cff" />
        <div style={{ fontSize: 15, fontWeight: 500 }}>Gerenciar Bases</div>
        <div style={{ display: "flex", gap: 6, marginLeft: 12, flex: 1 }}>
          {tabs.map(([k, lbl]) => (
            <span
              key={k}
              onClick={() => setTab(k)}
              style={{
                padding: "5px 12px", borderRadius: 8, fontSize: 12, fontWeight: 500, cursor: "pointer",
                background: "rgba(14, 15, 40, 0.16)",
                color: currentTab === k ? "#c7d2fe" : C.faint,
                border: `1px solid ${currentTab === k ? "rgba(99,102,241,0.4)" : "transparent"}`,
              }}
            >
              {lbl}
            </span>
          ))}
        </div>
        {iconBtn(onClose, "Fechar", <X size={18} />)}
      </div>

      {/* Área de conteúdo com altura flexível: rola por dentro, mantendo o
          modal com altura constante ao alternar abas/views. */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
      {currentTab === "optout" ? (
        <div style={{ padding: 22, minHeight: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
          <OptOutManager />
        </div>
      ) : view === "mapping" ? (
        <div style={{ padding: 22 }}>
          <div
            onClick={() => nextInQueue()}
            style={{ display: "inline-flex", alignItems: "center", gap: 5, color: C.faint, cursor: "pointer", fontSize: 12, marginBottom: 14 }}
          >
            <ChevronLeft size={14} /> Pular mapeamento
          </div>
          <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 4 }}>
            Mapear colunas — {current.originalFilename} {queue.length > 1 ? `(${qIdx + 1}/${queue.length})` : ""}
          </div>
          <div style={{ fontSize: 12, color: C.faint, marginBottom: 16 }}>
            O telefone é obrigatório. As demais colunas ficam disponíveis como variáveis do template.
          </div>

          <label style={{ fontSize: 11, fontWeight: 600, color: C.faint }}>Nome da base</label>
          <input
            value={mapName}
            onChange={(e) => setMapName(e.target.value)}
            style={inputStyle}
          />

          <div style={{ display: "flex", gap: 14, marginTop: 14 }}>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.faint }}>Coluna de telefone *</label>
              <CustomSelect
                value={phoneCol}
                placeholder="Selecione…"
                onChange={(v) => { setPhoneCol(v); setVarCols((prev) => prev.filter((c) => c !== v)); }}
                options={(current.columns || []).map((c) => ({ value: c, label: c }))}
              />
            </div>
            <div style={{ flex: 1 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.faint }}>Coluna de nome (opcional)</label>
              <CustomSelect
                value={nameCol}
                placeholder="—"
                onChange={(v) => { setNameCol(v); setVarCols((prev) => prev.filter((c) => c !== v)); }}
                options={[{ value: "", label: "— (nenhuma)" }, ...(current.columns || []).map((c) => ({ value: c, label: c }))]}
              />
            </div>
          </div>

          {remainingCols.length > 0 && (
            <div style={{ marginTop: 14 }}>
              <label style={{ fontSize: 11, fontWeight: 600, color: C.faint }}>Outras colunas (variáveis)</label>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginTop: 8 }}>
                {remainingCols.map((c) => {
                  const on = varCols.includes(c);
                  return (
                    <span
                      key={c}
                      onClick={() => setVarCols((prev) => (on ? prev.filter((x) => x !== c) : [...prev, c]))}
                      style={{
                        padding: "5px 10px", borderRadius: 8, fontSize: 12, cursor: "pointer",
                        background: on ? C.accentBg : "rgba(255,255,255,0.03)",
                        border: `1px solid ${on ? "rgba(99,102,241,0.5)" : C.border}`,
                        color: on ? "#c7d2fe" : C.faint,
                      }}
                    >
                      {c}
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {(current.sampleRows || []).length > 0 && (
            <div style={{ marginTop: 18, overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 8 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 11 }}>
                <thead>
                  <tr>
                    {(current.columns || []).map((c) => (
                      <th key={c} style={{ ...thStyle, color: c === phoneCol ? C.accent : C.faint }}>{c}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {current.sampleRows.slice(0, 5).map((r, i) => (
                    <tr key={i}>
                      {(current.columns || []).map((c) => (
                        <td key={c} style={tdStyle}>{String(r[c] ?? "")}</td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div style={{ display: "flex", gap: 10, marginTop: 22, justifyContent: "flex-end" }}>
            <button onClick={() => nextInQueue()} style={btnGhost}>Cancelar</button>
            <button
              onClick={processBase}
              disabled={savingMap}
              onMouseEnter={(e) => { if (!savingMap) e.currentTarget.style.background = "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)"; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)"; }}
              style={{ ...btnPrimary, cursor: savingMap ? "default" : "pointer", opacity: savingMap ? 0.6 : 1 }}
            >
              {savingMap ? "Enviando…" : "Processar base"}
            </button>
          </div>
        </div>
      ) : (
        <div style={{ padding: 22, minHeight: "100%", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
          {/* toolbar */}
          <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center" }}>
            <div style={{ position: "relative", flex: 1 }}>
              <Search size={14} style={{ position: "absolute", left: 10, top: 10, color: C.faint }} />
              <input
                placeholder="Buscar base pelo nome…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ ...inputStyle, marginTop: 0, paddingLeft: 32 }}
              />
            </div>
            <span onClick={() => cb.list({ search })} title="Recarregar" style={{ cursor: "pointer", color: C.faint, padding: 6 }}>
              <RefreshCw size={15} />
            </span>
          </div>

          {/* upload zone */}
          {canManage && (
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={onDrop}
              onClick={() => fileRef.current && fileRef.current.click()}
              style={{
                border: `2px dashed ${dragOver ? C.accent : C.border}`,
                borderRadius: 10, padding: "20px", textAlign: "center", cursor: "pointer",
                background: dragOver ? C.accentBg : C.panel, marginBottom: 18, transition: "all .15s",
              }}
            >
              <input
                ref={fileRef}
                type="file"
                accept=".xlsx,.xls,.csv"
                multiple
                style={{ display: "none" }}
                onChange={(e) => { doUpload(e.target.files); e.target.value = ""; }}
              />
              {uploading ? (
                <div style={{ color: C.accent, fontSize: 13 }}><CircularProgress size={16} style={{ color: C.accent, marginRight: 8 }} />Enviando…</div>
              ) : (
                <div style={{ color: C.faint, fontSize: 13 }}>
                  <Upload size={20} color={C.accent} style={{ marginBottom: 6 }} />
                  <div><strong style={{ color: C.text }}>Clique</strong> ou arraste planilhas (XLSX, XLS, CSV) — vários arquivos</div>
                </div>
              )}
            </div>
          )}

          {/* list */}
          {loading ? (
            <div style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", padding: 30 }}><CircularProgress size={22} style={{ color: C.accent }} /></div>
          ) : bases.length === 0 ? (
            <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: 30, color: C.faint, fontSize: 13 }}>
              <AlertCircle size={22} style={{ marginBottom: 8, opacity: 0.6 }} /><div>Nenhuma base cadastrada.</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto", border: `1px solid ${C.border}`, borderRadius: 10 }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                <thead>
                  <tr>
                    {["Nome", "Arquivo", "Contatos", "Válidos", "Inválidos", "Duplic.", "Status", "Data", "Usuário", "Ações"].map((h) => (
                      <th key={h} style={thStyle}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {bases.map((b) => {
                    const mappable = b.status === "awaiting_mapping";
                    return (
                      <tr key={b.id} style={{ borderTop: `1px solid ${C.border}` }}>
                        <td style={{ ...tdStyle, fontWeight: 600, color: C.text }}>{b.name}</td>
                        <td style={{ ...tdStyle, color: C.faint, maxWidth: 160, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{b.originalFilename}</td>
                        <td style={tdStyle}>{b.totalRows}</td>
                        <td style={{ ...tdStyle, color: "#34d399" }}>{b.validRows}</td>
                        <td style={{ ...tdStyle, color: "#f87171" }}>{b.invalidRows}</td>
                        <td style={{ ...tdStyle, color: "#fb923c" }}>{b.duplicateRows}</td>
                        <td style={tdStyle}><Badge status={b.status} error={b.processingError} /></td>
                        <td style={{ ...tdStyle, color: C.faint }}>{fmtDate(b.createdAt)}</td>
                        <td style={{ ...tdStyle, color: C.faint }}>{b.createdBy ? b.createdBy.name : "—"}</td>
                        <td style={tdStyle}>
                          <div style={{ display: "flex", alignItems: "center" }}>
                            {canManage && mappable && iconBtn(() => openPreviewThenMap(b), "Mapear", <Edit2 size={14} color={C.accent} />)}
                            {canManage && !mappable && iconBtn(() => handleRename(b), "Renomear", <Edit2 size={14} />)}
                            {b.hasErrorReport && iconBtn(() => handleErrorReport(b), "Relatório de erros", <Download size={14} color="#fb923c" />)}
                            {canManage && b.status !== "archived" && iconBtn(() => handleArchive(b), "Arquivar", <Archive size={14} />)}
                            {canManage && iconBtn(() => handleDelete(b), "Excluir", <Trash2 size={14} color="#f87171" />)}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
      </div>

      {/* Modal custom de ação (renomear / arquivar / excluir) */}
      {dialog && (() => {
        const b = dialog.base || {};
        const meta = {
          rename: { title: "Renomear base", confirmLabel: "Salvar", danger: false },
          archive: {
            title: "Arquivar base",
            confirmLabel: "Arquivar",
            danger: false,
            message: `Arquivar a base "${b.name}"? Ela deixará de aparecer no seletor de disparo.`,
          },
          delete: {
            title: "Excluir base",
            confirmLabel: "Excluir",
            danger: true,
            message: `Excluir a base "${b.name}"? Esta ação não pode ser desfeita.`,
          },
        }[dialog.kind];
        const grad = meta.danger
          ? "linear-gradient(135deg, #f87171 0%, #ef4444 100%)"
          : "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)";
        const gradHover = meta.danger
          ? "linear-gradient(135deg, #ef4444 0%, #dc2626 100%)"
          : "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)";
        return (
          <div
            onClick={() => { if (!dialogBusy) setDialog(null); }}
            // Escape fecha SÓ este overlay — sem o stopPropagation, o keydown
            // sobe até o Dialog MUI de fora e fecha o Gerenciar Bases inteiro.
            onKeyDown={(e) => {
              if (e.key !== "Escape") return;
              e.stopPropagation();
              if (!dialogBusy) setDialog(null);
            }}
            // Arquivar/excluir não têm input: foca o overlay pro Escape cair aqui.
            // (No renomear o autoFocus do input já venceu → contains() pula.)
            tabIndex={-1}
            ref={(el) => { if (el && !el.contains(document.activeElement)) el.focus(); }}
            style={{
              position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1500,
              display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
              background: "rgba(0,0,0,0.55)", outline: "none",
            }}
          >
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                width: "min(420px, 94vw)", background: "#141824",
                border: `1px solid ${C.border}`, borderRadius: 14,
                boxShadow: "0 24px 64px rgba(0,0,0,0.5)", padding: 22, color: C.text,
              }}
            >
              <div style={{ fontSize: 15, fontWeight: 600, marginBottom: dialog.kind === "rename" ? 14 : 8 }}>
                {meta.title}
              </div>
              {dialog.kind === "rename" ? (
                <>
                  <label style={{ fontSize: 11, fontWeight: 600, color: C.faint, display: "block", marginBottom: 6 }}>
                    Novo nome da base
                  </label>
                  <input
                    autoFocus
                    value={dialog.value}
                    onChange={(e) => setDialog({ ...dialog, value: e.target.value })}
                    onKeyDown={(e) => { if (e.key === "Enter") confirmDialog(); }}
                    style={{ ...inputStyle, marginTop: 0 }}
                  />
                </>
              ) : (
                <div style={{ fontSize: 13, lineHeight: 1.55, color: C.faint }}>{meta.message}</div>
              )}
              <div style={{ display: "flex", gap: 10, justifyContent: "flex-end", marginTop: 22 }}>
                <button onClick={() => setDialog(null)} disabled={dialogBusy} style={btnGhost}>
                  Cancelar
                </button>
                <button
                  onClick={confirmDialog}
                  disabled={dialogBusy}
                  onMouseEnter={(e) => { if (!dialogBusy) e.currentTarget.style.background = gradHover; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = grad; }}
                  style={{ ...btnPrimary, background: grad, opacity: dialogBusy ? 0.6 : 1, cursor: dialogBusy ? "default" : "pointer" }}
                >
                  {dialogBusy ? "…" : meta.confirmLabel}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal de relatório de erros (linhas inválidas/duplicadas) */}
      {errReport && (
        <div
          onClick={() => setErrReport(null)}
          // Escape fecha só o relatório (não o Gerenciar Bases) — ver overlay acima.
          onKeyDown={(e) => {
            if (e.key !== "Escape") return;
            e.stopPropagation();
            setErrReport(null);
          }}
          style={{
            position: "fixed", top: 0, left: 0, right: 0, bottom: 0, zIndex: 1500,
            display: "flex", alignItems: "center", justifyContent: "center", padding: 24,
            background: "rgba(0,0,0,0.55)",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            tabIndex={-1}
            ref={(el) => { if (el) el.focus(); }}
            style={{
              outline: "none",
              width: "min(680px, 96vw)", maxHeight: "82vh", background: "#141824",
              border: `1px solid ${C.border}`, borderRadius: 14,
              boxShadow: "0 24px 64px rgba(0,0,0,0.5)", color: C.text,
              display: "flex", flexDirection: "column", overflow: "hidden",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "16px 20px", borderBottom: `1px solid ${C.border}`, flexShrink: 0 }}>
              <AlertCircle size={17} color="#fb923c" />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 14, fontWeight: 500 }}>Relatório de erros</div>
                <div style={{ fontSize: 11, color: C.faint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {errReport.base.name} · {errReport.count} linha{errReport.count === 1 ? "" : "s"} com problema
                </div>
              </div>
              <span onClick={() => setErrReport(null)} style={{ cursor: "pointer", color: C.faint, padding: 4, display: "inline-flex" }}>
                <X size={18} />
              </span>
            </div>
            <div style={{ flex: 1, minHeight: 0, overflowY: "auto" }}>
              {errReport.loading && errReport.rows.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center" }}><CircularProgress size={22} style={{ color: C.accent }} /></div>
              ) : errReport.rows.length === 0 ? (
                <div style={{ padding: 30, textAlign: "center", color: C.faint, fontSize: 13 }}>Nenhuma linha com problema.</div>
              ) : (
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
                  <thead>
                    <tr>
                      {["Linha", "Nome", "Telefone", "Motivo"].map((h) => <th key={h} style={thStyle}>{h}</th>)}
                    </tr>
                  </thead>
                  <tbody>
                    {errReport.rows.map((r) => (
                      <tr key={r.id}>
                        <td style={{ ...tdStyle, color: C.faint }}>{r.rowIndex}</td>
                        <td style={tdStyle}>{r.name || "—"}</td>
                        <td style={{ ...tdStyle, fontFamily: "monospace" }}>{r.phoneNormalized || r.phoneRaw || "—"}</td>
                        <td style={tdStyle}>
                          <span style={{
                            display: "inline-block", padding: "2px 8px", borderRadius: 999, fontSize: 11, fontWeight: 600,
                            background: r.isDuplicate ? "rgba(251,146,60,0.15)" : "rgba(248,113,113,0.15)",
                            color: r.isDuplicate ? "#fb923c" : "#f87171",
                          }}>
                            {reasonOf(r)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
            {errReport.hasMore && (
              <div style={{ padding: 12, textAlign: "center", borderTop: `1px solid ${C.border}`, flexShrink: 0 }}>
                <button onClick={loadMoreErrors} disabled={errReport.loading} style={btnGhost}>
                  {errReport.loading ? "Carregando…" : "Carregar mais"}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </Dialog>
  );

  // Abre o preview de uma base já enviada (status awaiting_mapping) e entra no mapeamento.
  async function openPreviewThenMap(b) {
    try {
      const data = await cb.getPreview(b.id);
      const item = { ...b, columns: data.columns || [], sampleRows: data.sampleRows || [] };
      setQueue([item]);
      setQIdx(0);
      startMappingFor(item);
      setView("mapping");
    } catch (err) {
      toastError(err);
    }
  }
};

const inputStyle = {
  width: "100%", marginTop: 6, padding: "9px 12px", borderRadius: 8,
  background: "rgba(255,255,255,0.04)", border: "1px solid rgba(255,255,255,0.1)",
  color: "rgba(255,255,255,0.9)", fontSize: 13, outline: "none", boxSizing: "border-box",
};
const thStyle = {
  textAlign: "left", padding: "9px 12px", fontSize: 11, fontWeight: 600,
  color: "rgba(255,255,255,0.5)", background: "rgba(255,255,255,0.02)", whiteSpace: "nowrap",
};
const tdStyle = { padding: "9px 12px", color: "rgba(255,255,255,0.8)" };
const btnPrimary = {
  height: 36, padding: "0 14px", borderRadius: 10, border: "none",
  background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
  color: "#ffffff", fontSize: "0.875rem", fontWeight: 500, textTransform: "none",
  cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8,
  whiteSpace: "nowrap", transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)", flexShrink: 0,
};
const btnGhost = {
  padding: "9px 18px", borderRadius: 9, border: "1px solid rgba(255,255,255,0.12)",
  background: "transparent", color: "rgba(255,255,255,0.7)", fontSize: 13, fontWeight: 400, cursor: "pointer",
};

export default ContactBasesModal;
